import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ApiService from '../services/api';
import './TeacherSearchDialog.css';
import { tracingChannel } from 'diagnostics_channel';
import { useClassroomData, processedClassroomData } from '../data/classroomData';

interface TeacherSearchDialogProps {
  onClose: () => void;
  targetClassroomId: string;
}

interface TeacherSearchResult {
  id: string;
  name: string;
  currentClassroom?: {
    id: string;
    name: string;
    studentCount: number;
    teacherCount: number;
  };
}

const TeacherSearchDialog: React.FC<TeacherSearchDialogProps> = ({ 
  onClose, 
  targetClassroomId 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<TeacherSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [transferringTeacher, setTransferringTeacher] = useState<string | null>(null);
  
  const classroomData = useClassroomData().data;
  const { sections, specialCategories } = useMemo(()=> processedClassroomData(classroomData), [classroomData]);
  const refreshData = useClassroomData().refetch;

  const inputRef = useRef<HTMLInputElement>(null);

  // Focus on search input when dialog opens
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // Create a function to search for teachers in the existing data
  const searchTeachers = (term: string): TeacherSearchResult[] => {
    if (!term || term.length < 2) return [];
    
    const results: TeacherSearchResult[] = [];
    const lowerTerm = term.toLowerCase();
    
    // Search through regular classrooms
    sections.forEach(section => {
      section.classrooms.forEach(classroom => {
        if (classroom.teachers) {
          classroom.teachers.filter(teach => teach.sc_id).forEach(teacher => {
            if (teacher.name.toLowerCase().includes(lowerTerm)) {
              results.push({
                id: teacher.sc_id!,
                name: teacher.name,
                currentClassroom: {
                  id: classroom.id,
                  name: classroom.name,
                  studentCount: classroom.studentCount,
                  teacherCount: classroom.teacherCount
                }
              });
            }
          });
        }
      });
    });
    
    // Search through special categories (office, unassigned)
    specialCategories.forEach(classroom => {
      if (classroom.teachers) {
        classroom.teachers.filter(teach => teach.sc_id).forEach(teacher => {
          if (teacher.name.toLowerCase().includes(lowerTerm)) {
            results.push({
              id: teacher.sc_id!,
              name: teacher.name,
              currentClassroom: {
                id: classroom.id,
                name: classroom.name,
                studentCount: classroom.studentCount,
                teacherCount: classroom.teacherCount
              }
            });
          }
        });
      }
    });
    
    return results;
  };

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    if (value.length < 2) {
      setSearchResults([]);
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      const results = searchTeachers(value);
      setSearchResults(results);
    } catch (err) {
      console.error('Error searching teachers:', err);
      setError('Failed to search for teachers');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle teacher selection
  const handleTeacherSelect = async (teacherId: string) => {
    try {
      setTransferringTeacher(teacherId);
      setError(null);
      
      // Find the teacher in our data
      const teacher = searchResults.find(t => t.id === teacherId);
      if (!teacher) {
        throw new Error('Teacher not found');
      }
      
      // Check if the teacher is already in the target classroom
      if (teacher.currentClassroom?.id === targetClassroomId) {
        throw new Error('Teacher is already in this classroom');
      }
      
      await ApiService.checkInTeacher({
        teacherId,
        classroomId: targetClassroomId
      });
      
      // Refresh classroom data
      await refreshData();
      
      // Close dialog
      onClose();
    } catch (err) {
      console.error('Error transferring teacher:', err);
      setError(err instanceof Error ? err.message : 'Failed to transfer teacher');
      setTransferringTeacher(null);
    }
  };

  // Handle clicks outside of the dialog to close it
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle escape key to close dialog
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  return (
    <div className="teacher-search-component teacher-search-overlay" onClick={handleOverlayClick}>
      <div className="teacher-search-dialog">
        <div className="teacher-search-header">
          <h3>Add Teacher to Classroom</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <div className="teacher-search-body">
          <div className="search-input-container">
            <input
              ref={inputRef}
              type="text"
              placeholder="Start typing a teacher's name..."
              value={searchTerm}
              onChange={handleSearchChange}
              disabled={isLoading || !!transferringTeacher}
              className="teacher-search-input"
            />
            {isLoading && <div className="search-loading-indicator"></div>}
          </div>
          
          {error && <div className="search-error">{error}</div>}
          
          {searchResults.length > 0 ? (
            <div className="search-results">
              {searchResults.map(teacher => (
                <div 
                  key={teacher.id} 
                  className="teacher-result-item"
                  onClick={() => handleTeacherSelect(teacher.id)}
                >
                  <div className="teacher-result-name">
                    {teacher.name}
                    {transferringTeacher === teacher.id && 
                     <span className="transferring-indicator">Transferring...</span>}
                  </div>
                  {teacher.currentClassroom && (
                    <div className="teacher-current-location">
                      Currently in: {teacher.currentClassroom.name}
                      <span className="classroom-stats">
                        ({teacher.currentClassroom.studentCount}S / {teacher.currentClassroom.teacherCount}T)
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            searchTerm.length >= 2 && !isLoading && (
              <div className="no-results">
                No teachers found matching "{searchTerm}"
              </div>
            )
          )}
          
          {searchTerm.length < 2 && (
            <div className="search-instructions">
              Type at least 2 characters to search for teachers
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeacherSearchDialog;